import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit2, X, StickyNote, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { config } from '../config';

interface Note {
  id: number;
  title: string;
  content: string;
  color: string;
  completed: boolean;
  created_at: string;
  updated_at: string;
}

const COLORS = [
  { name: 'yellow', bg: 'bg-yellow-100', border: 'border-yellow-300', header: 'bg-yellow-200', text: 'text-yellow-900' },
  { name: 'blue', bg: 'bg-blue-100', border: 'border-blue-300', header: 'bg-blue-200', text: 'text-blue-900' },
  { name: 'green', bg: 'bg-green-100', border: 'border-green-300', header: 'bg-green-200', text: 'text-green-900' },
  { name: 'pink', bg: 'bg-pink-100', border: 'border-pink-300', header: 'bg-pink-200', text: 'text-pink-900' },
  { name: 'purple', bg: 'bg-purple-100', border: 'border-purple-300', header: 'bg-purple-200', text: 'text-purple-900' },
  { name: 'orange', bg: 'bg-orange-100', border: 'border-orange-300', header: 'bg-orange-200', text: 'text-orange-900' },
];

const getColorClasses = (color: string) => {
  return COLORS.find(c => c.name === color) || COLORS[0];
};

const StickyNotes: React.FC = () => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '', color: 'yellow' });
  const [deleteNoteId, setDeleteNoteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const NOTES_PER_PAGE = 12;
  const [selectMode, setSelectMode] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState<number[]>([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchNotes = async () => {
    try {
      const res = await fetch(`${config.apiUrl}/sticky-notes`, { headers });
      const data = await res.json();
      setNotes(data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchNotes(); }, []);

  const openNewNote = () => {
    setEditingNote(null);
    setFormData({ title: '', content: '', color: 'yellow' });
    setShowModal(true);
  };

  const openEditNote = (note: Note) => {
    setEditingNote(note);
    setFormData({ title: note.title, content: note.content, color: note.color });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (editingNote) {
        const res = await fetch(`${config.apiUrl}/sticky-notes/${editingNote.id}`, {
          method: 'PUT', headers, body: JSON.stringify(formData)
        });
        const updated = await res.json();
        setNotes(prev => prev.map(n => n.id === editingNote.id ? updated : n));
      } else {
        const res = await fetch(`${config.apiUrl}/sticky-notes`, {
          method: 'POST', headers, body: JSON.stringify(formData)
        });
        const created = await res.json();
        setNotes(prev => [created, ...prev]);
      }
      setShowModal(false);
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async (id: number) => {
    try {
      const res = await fetch(`${config.apiUrl}/sticky-notes/${id}/toggle`, {
        method: 'PATCH', headers
      });
      const updated = await res.json();
      setNotes(prev => prev.map(n => n.id === id ? updated : n));
    } catch (error) {
      console.error('Error toggling note:', error);
    }
  };

  const confirmDelete = async () => {
    if (!deleteNoteId) return;
    try {
      await fetch(`${config.apiUrl}/sticky-notes/${deleteNoteId}`, { method: 'DELETE', headers });
      setNotes(prev => prev.filter(n => n.id !== deleteNoteId));
    } catch (error) {
      console.error('Error deleting note:', error);
    } finally {
      setDeleteNoteId(null);
    }
  };

  const toggleSelectNote = (id: number) => {
    setSelectedNotes(prev => prev.includes(id) ? prev.filter(n => n !== id) : [...prev, id]);
  };

  const confirmBulkDelete = async () => {
    try {
      await Promise.all(selectedNotes.map(id =>
        fetch(`${config.apiUrl}/sticky-notes/${id}`, { method: 'DELETE', headers })
      ));
      setNotes(prev => prev.filter(n => !selectedNotes.includes(n.id)));
    } catch (error) {
      console.error('Error bulk deleting notes:', error);
    } finally {
      setSelectedNotes([]);
      setSelectMode(false);
      setShowBulkDeleteModal(false);
    }
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedNotes([]);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const pendingCount = notes.filter(n => !n.completed).length;
  const completedCount = notes.filter(n => n.completed).length;
  const totalPages = Math.ceil(notes.length / NOTES_PER_PAGE);
  const paginatedNotes = notes.slice((currentPage - 1) * NOTES_PER_PAGE, currentPage * NOTES_PER_PAGE);

  return (
    <div className="space-y-0">
      {/* Notebook Header */}
      <div className="bg-gradient-to-r from-amber-700 via-amber-600 to-yellow-600 rounded-t-2xl px-6 py-5 shadow-lg">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-lg flex items-center justify-center">
              <StickyNote size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Mi Libreta</h1>
              <p className="text-amber-200 text-xs">
                {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''} · {completedCount} completada{completedCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {selectMode ? (
              <>
                <button
                  onClick={exitSelectMode}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur text-white rounded-lg transition text-sm font-medium"
                >
                  <X size={16} />
                  Cancelar
                </button>
                {selectedNotes.length > 0 && (
                  <button
                    onClick={() => setShowBulkDeleteModal(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-red-500/80 hover:bg-red-500 backdrop-blur text-white rounded-lg transition text-sm font-medium"
                  >
                    <Trash2 size={16} />
                    Eliminar ({selectedNotes.length})
                  </button>
                )}
              </>
            ) : (
              <>
                {notes.length > 0 && (
                  <button
                    onClick={() => setSelectMode(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur text-white/80 rounded-lg transition text-sm font-medium"
                  >
                    <Check size={16} />
                    Seleccionar
                  </button>
                )}
                <button
                  onClick={openNewNote}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur text-white rounded-lg transition text-sm font-medium"
                >
                  <Plus size={16} />
                  Nueva Nota
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Notebook Body */}
      <div className="bg-amber-50/50 border-x-2 border-b-2 border-amber-200 rounded-b-2xl min-h-[60vh] p-6 relative"
        style={{ backgroundImage: 'repeating-linear-gradient(transparent, transparent 27px, #f3d9a4 28px)', backgroundSize: '100% 28px' }}
      >
        {/* Notebook spine decoration */}
        <div className="absolute left-8 top-0 bottom-0 w-px border-l-2 border-red-300/40" />

        {notes.length === 0 ? (
          <div className="text-center py-20 relative z-10">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-inner">
              <StickyNote size={36} className="text-amber-400" />
            </div>
            <p className="text-amber-700 font-medium">Tu libreta está vacía</p>
            <p className="text-amber-500 text-sm mt-1">Agrega tu primera nota para empezar</p>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 relative z-10 pl-4">
          {paginatedNotes.map(note => {
            const c = getColorClasses(note.color);
            return (
              <div
                key={note.id}
                className={`${note.completed ? 'bg-gray-100' : c.bg} rounded-lg shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden relative ${note.completed ? 'opacity-70' : ''} ${selectMode && selectedNotes.includes(note.id) ? 'ring-2 ring-amber-500 ring-offset-2' : ''}`}
                style={{ minHeight: '180px' }}
                onClick={selectMode ? (e) => { e.stopPropagation(); toggleSelectNote(note.id); } : undefined}
              >
                {/* Action buttons - top right */}
                <div className="absolute top-2 right-2 flex items-center gap-1 z-10">
                  {selectMode ? (
                    <div className={`w-6 h-6 flex items-center justify-center rounded border-2 transition-colors ${
                      selectedNotes.includes(note.id)
                        ? 'bg-amber-500 border-amber-500 text-white'
                        : 'border-gray-400 bg-white/60'
                    }`}>
                      {selectedNotes.includes(note.id) && <Check size={13} />}
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleToggle(note.id); }}
                        className={`w-6 h-6 flex items-center justify-center rounded-full transition-colors ${
                          note.completed
                            ? 'bg-green-500 text-white hover:bg-green-600'
                            : 'bg-black/10 text-gray-500 hover:bg-green-500 hover:text-white'
                        }`}
                        title={note.completed ? 'Desmarcar' : 'Marcar como hecho'}
                      >
                        <Check size={13} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteNoteId(note.id); }}
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-black/10 hover:bg-red-500 hover:text-white text-gray-500 transition-colors"
                        title="Eliminar nota"
                      >
                        <X size={13} />
                      </button>
                    </>
                  )}
                </div>

                {/* Colored top strip */}
                <div className={`h-1.5 ${note.completed ? 'bg-gray-300' : c.header}`} />

                {/* Card body - clickable to edit */}
                <div
                  className="px-4 pt-3 pb-2 cursor-pointer flex flex-col h-full"
                  onClick={selectMode ? undefined : () => openEditNote(note)}
                >
                  <h3 className={`font-bold text-sm truncate pr-14 mb-1.5 overflow-hidden ${note.completed ? 'line-through text-gray-400' : c.text}`}>
                    {note.title || 'Sin título'}
                  </h3>
                  <p className={`text-[13px] whitespace-pre-wrap break-all line-clamp-5 flex-1 leading-relaxed overflow-hidden ${note.completed ? 'line-through text-gray-400' : c.text + ' opacity-80'}`}>
                    {note.content || 'Sin contenido'}
                  </p>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-black/5">
                    <p className="text-[10px] text-gray-400">
                      {new Date(note.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <Edit2 size={12} className="text-gray-400" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-6 relative z-10 pl-4">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/80 shadow-sm border border-amber-200 text-amber-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition ${
                  currentPage === page
                    ? 'bg-amber-600 text-white shadow-sm'
                    : 'bg-white/80 shadow-sm border border-amber-200 text-amber-700 hover:bg-white'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/80 shadow-sm border border-amber-200 text-amber-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800">
                {editingNote ? 'Editar Nota' : 'Nueva Nota'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Título</label>
                  <span className={`text-xs ${formData.title.length >= 50 ? 'text-red-500' : 'text-gray-400'}`}>{formData.title.length}/50</span>
                </div>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => e.target.value.length <= 50 && setFormData({ ...formData, title: e.target.value })}
                  placeholder="Título de la nota..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  maxLength={50}
                  autoFocus
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Contenido</label>
                  <span className={`text-xs ${formData.content.length >= 500 ? 'text-red-500' : 'text-gray-400'}`}>{formData.content.length}/500</span>
                </div>
                <textarea
                  value={formData.content}
                  onChange={(e) => e.target.value.length <= 500 && setFormData({ ...formData, content: e.target.value })}
                  placeholder="Escribe tu nota aquí..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                  maxLength={500}
                  rows={5}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                <div className="flex gap-2">
                  {COLORS.map(c => (
                    <button
                      key={c.name}
                      onClick={() => setFormData({ ...formData, color: c.name })}
                      className={`w-8 h-8 rounded-full ${c.header} border-2 transition-transform ${
                        formData.color === c.name ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-105'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-5 py-2.5 text-sm font-medium bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? 'Guardando...' : editingNote ? 'Guardar' : 'Crear Nota'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteNoteId && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={24} className="text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">Eliminar Nota</h3>
              <p className="text-sm text-gray-500">¿Estás seguro que deseas eliminar esta nota? Esta acción no se puede deshacer.</p>
            </div>
            <div className="flex border-t border-gray-100">
              <button
                onClick={() => setDeleteNoteId(null)}
                className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition border-l border-gray-100"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={24} className="text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">Eliminar {selectedNotes.length} Nota{selectedNotes.length !== 1 ? 's' : ''}</h3>
              <p className="text-sm text-gray-500">¿Estás seguro que deseas eliminar las notas seleccionadas? Esta acción no se puede deshacer.</p>
            </div>
            <div className="flex border-t border-gray-100">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="flex-1 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={confirmBulkDelete}
                className="flex-1 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition border-l border-gray-100"
              >
                Eliminar Todas
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StickyNotes;
